/**
 * Shared HTTP plumbing for adapters (R5: "politeness by construction").
 *
 * Every outbound request in the app goes through here, which is what makes the
 * rate limiting and backoff guarantees checkable rather than aspirational. The
 * Sleeper limit in particular is a hard requirement (NFR-5, §4.1: stay well
 * under 1000 calls/minute or risk an IP block), and a draft-day IP block would
 * take out the one feature that matters.
 */

export interface FetchOptions {
  timeoutMs?: number;
  /** Retries on transient failures only — never on a 4xx (R5: hard-fail
   *  rather than retry-storm). */
  retries?: number;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export class HttpError extends Error {
  readonly status: number;
  readonly url: string;

  constructor(status: number, url: string, message?: string) {
    super(message ?? `HTTP ${status} from ${url}`);
    this.name = 'HttpError';
    this.status = status;
    this.url = url;
  }
}

/**
 * A token-bucket limiter, one instance per host.
 *
 * Deliberately a hard gate rather than a best-effort hint: `acquire()` waits
 * rather than returning false, so a caller cannot accidentally opt out by
 * ignoring a return value.
 */
export class RateLimiter {
  private readonly intervalMs: number;
  private queue: Promise<void> = Promise.resolve();
  private last = 0;

  constructor(requestsPerSecond: number) {
    this.intervalMs = 1000 / Math.max(requestsPerSecond, 0.01);
  }

  acquire(): Promise<void> {
    const run = this.queue.then(async () => {
      const wait = this.last + this.intervalMs - Date.now();
      if (wait > 0) await sleep(wait);
      this.last = Date.now();
    });
    // Keep the chain alive even if a caller's continuation throws.
    this.queue = run.catch(() => undefined);
    return run;
  }
}

/** NFR-5: a global throttle across *all* Sleeper calls, not per-endpoint. */
export const sleeperLimiter = new RateLimiter(1);

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * `fetch` with a timeout, bounded retries, and exponential backoff.
 *
 * Retries only 429, 5xx, and network failures. A 404 means the league does not
 * exist and retrying it four times is just rudeness with extra steps.
 */
export async function fetchWithRetry(
  url: string,
  options: FetchOptions = {},
): Promise<Response> {
  const { timeoutMs = 10_000, retries = 2 } = options;
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    const onAbort = () => controller.abort();
    options.signal?.addEventListener('abort', onAbort, { once: true });

    try {
      const res = await fetch(url, {
        signal: controller.signal,
        headers: options.headers,
        // Never send cookies or credentials to a third party (FR-58 in spirit,
        // and it keeps the browser from attaching anything the user has for
        // that domain).
        credentials: 'omit',
        redirect: 'follow',
      });

      if (res.ok) return res;
      if (res.status === 429 || res.status >= 500) {
        lastError = new HttpError(res.status, url);
      } else {
        throw new HttpError(res.status, url);
      }
    } catch (err) {
      if (err instanceof HttpError && err.status < 500 && err.status !== 429) throw err;
      if (options.signal?.aborted) throw err;
      lastError = err;
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', onAbort);
    }

    if (attempt < retries) {
      await sleep(2 ** attempt * 1000 + Math.random() * 250);
    }
  }

  throw lastError instanceof Error ? lastError : new Error(`Request failed: ${url}`);
}

/**
 * Parse JSON by content, not by content-type header.
 *
 * Required, not fastidious: the FFC ADP endpoint serves JSON with
 * `content-type: text/html` (verified 2026-07-27), so trusting the header would
 * reject a perfectly good response.
 */
export async function parseJsonByContent<T>(res: Response): Promise<T> {
  const text = await res.text();
  const trimmed = text.trimStart();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    // Describe the shape rather than quoting the body. These messages surface
    // in the UI footer, and echoing 60 characters of someone's markup there is
    // both unreadable and a small way for fetched content to reach the page.
    const shape = /^<!doctype|^<html|^<\?xml|^</i.test(trimmed)
      ? 'an HTML page'
      : trimmed === ''
        ? 'an empty response'
        : 'a non-JSON response';
    throw new Error(`Expected JSON but got ${shape}.`);
  }
  return JSON.parse(text) as T;
}
